import {extend} from 'common/util';
import {debug} from 'common/report/util';
import {reporter} from 'common/report/error-reporting';
import {insertAtIndex, removeByIndex, replaceByIndex, unique} from 'common/util/array';
import {hasUserFilters} from 'common/util/custom_events';
import {PRIMITIVE_MATH_TYPES, XAU_MATH_TYPES} from 'common/report/constants';
import {FILTER_DETERMINER_ALL, FILTER_DETERMINER_ANY} from 'common/report/insights/util/constants';
import {cohortEntryIsInvalidForDataGroup} from 'common/query-builder/query-builder/util/data-groups';
import {XAU_ONLY_MATH_TYPES} from 'common/report/constants';
import {INSIGHTS_COHORT_TYPE} from 'common/widgets/property-filter-menu/models/insights-filter';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {MetricType} from 'common/query-builder/query-block/constants';

import {FilterDeterminer, InsightsResourceType} from '../util/types';
import {Clause} from './clause';
import {GroupClause} from './group-clause';
import {CohortsClause} from './cohorts-clause';
import {TimeClause} from './time-clause';
import {BehaviorShowClause, FormulaShowClause, ShowClause, ShowClauseClassType} from './show-clause';
import {FormulaClause} from './formula-clause';
import {FilterClause} from './filter-clause';
import {ClauseType, ClauseTypeEnum, DeserializeReturn} from './types';

type BaseClauseType<AttributesType> = {
  attrs: AttributesType;
  extend: (attrs: Partial<AttributesType>) => AttributesType | BaseClauseType<AttributesType>;
  serialize: () => any;
  toBookmarkData: () => any;
  toUrlData: (params?: any) => any;
  valid: boolean;
};

export class Section<A, C extends BaseClauseType<A>> {
  clauses: C[];
  TYPE: ClauseTypeEnum;

  constructor(attrs: any = {}) {
    const clauseArgs = (Array.isArray(attrs) ? attrs : [attrs]).filter(this.validClauseFn);

    this.clauses = [...clauseArgs, ...(attrs.clauses || [])];
  }

  get attrs() {
    const {clauses} = this;
    return {clauses};
  }

  get valid() {
    return this.clauses.every((clause) => clause.valid);
  }

  // Another less repetitive but more confusing way to do this:
  // https://stackoverflow.com/questions/68232682/return-discriminated-union-from-factory-function-in-typescript
  // Sadly both solutions are error prone in that if you return the wrong
  // section type there will be no error thrown by the compiler.
  static create(sectionType: typeof ClauseType.Cohorts, attrs): CohortsSection;
  static create(sectionType: typeof ClauseType.Filter, attrs): FilterSection;
  static create(sectionType: typeof ClauseType.Formula, attrs): FormulaSection;
  static create(sectionType: typeof ClauseType.Group, attrs): GroupSection;
  static create(sectionType: typeof ClauseType.Show, attrs): ShowSection;
  static create(sectionType: typeof ClauseType.Time, attrs): TimeSection;
  static create(sectionType: ClauseTypeEnum, attrs): any;
  static create(sectionType: ClauseTypeEnum, attrs): AllSectionTypes {
    switch (sectionType) {
      case ShowSection.TYPE:
        return new ShowSection(attrs);
      case CohortsSection.TYPE:
        return new CohortsSection(attrs);
      case GroupSection.TYPE:
        return new GroupSection(attrs);
      case FilterSection.TYPE:
        return new FilterSection(attrs);
      case FormulaSection.TYPE:
        return new FormulaSection(attrs);
      case TimeSection.TYPE:
        return new TimeSection(attrs);
    }
  }
  static deserialize(sectionType: typeof ClauseType.Cohorts, clauseAttrs): DeserializeReturn<CohortsSection>;
  static deserialize(sectionType: typeof ClauseType.Filter, clauseAttrs): DeserializeReturn<FilterSection>;
  static deserialize(sectionType: typeof ClauseType.Formula, clauseAttrs): DeserializeReturn<FormulaSection>;
  static deserialize(sectionType: typeof ClauseType.Group, clauseAttrs): DeserializeReturn<GroupSection>;
  static deserialize(sectionType: typeof ClauseType.Show, clauseAttrs): DeserializeReturn<ShowSection>;
  static deserialize(sectionType: typeof ClauseType.Time, clauseAttrs): DeserializeReturn<TimeSection>;
  static deserialize(sectionType: ClauseTypeEnum, clauseAttrs): DeserializeReturn<AllSectionTypes>;
  static deserialize<T extends Obj>(
    sectionType: ClauseTypeEnum,
    clauseAttrs: T | T[] | Section<T, BaseClauseType<T>> | {clauses: T[]},
  ): Pick<Section<T, BaseClauseType<T>>, 'clauses'> {
    if (Array.isArray(clauseAttrs)) {
      clauseAttrs = {clauses: clauseAttrs};
    }

    if (clauseAttrs.clauses) {
      return Section.create(
        sectionType,
        extend(clauseAttrs, {
          clauses: clauseAttrs.clauses.map((clause) => Section.createClause(sectionType, clause)),
        }),
      );
    } else {
      return {clauses: []};
    }
  }

  static createClause(sectionType: ClauseTypeEnum, clause) {
    // TODO(CC): Remove value comparison
    if (Array.isArray(clause)) {
      // if feature flag hasn't been fetched, check sectionType
      if (sectionType === ClauseType.Group) {
        return Section.create(
          sectionType,
          clause.map((c) => Clause.create(sectionType, c)),
        );
      } else {
        const newClause = {...clause[0], filterOperator: undefined, filterValue: undefined, filterType: undefined};
        return Section.create(sectionType, Clause.create(sectionType, newClause));
      }
    } else {
      return Clause.create(sectionType, clause);
    }
  }

  validClauseFn(clause) {
    return [ShowClause, FilterClause, TimeClause, GroupClause, CohortsClause, FormulaClause].some(
      (clauseType) => clause instanceof clauseType,
    );
  }

  create(sectionType: ClauseTypeEnum, attrs: any) {
    return Section.create(sectionType, attrs);
  }

  serialize() {
    return this.clauses.map((c) => c.serialize());
  }

  toBookmarkData() {
    return this.clauses.map((c) => c.toBookmarkData());
  }

  toUrlData() {
    return this.clauses.map((c) => c.toUrlData());
  }

  validate(newSection: this): this {
    const valid = newSection.valid;

    if (!valid) {
      debug.warn(`${this.TYPE} section invalid:`, newSection);
    }

    return valid ? newSection : this;
  }

  hasClauses() {
    return !!this.clauses.length;
  }

  getLastClause() {
    return this.hasClauses() ? this.clauses[this.clauses.length - 1] : null;
  }

  appendClause(newClause) {
    return this.validate(
      Section.create(this.TYPE, extend(this, {clauses: insertAtIndex(this.clauses, this.clauses.length, newClause)})),
    );
  }

  insertClause(index, newClause) {
    return this.validate(
      Section.create(this.TYPE, extend(this, {clauses: insertAtIndex(this.clauses, index, newClause)})),
    );
  }

  replaceClause(index, newClause) {
    return this.validate(
      Section.create(this.TYPE, extend(this, {clauses: replaceByIndex(this.clauses, index, newClause)})),
    );
  }

  removeClause(index) {
    return this.validate(Section.create(this.TYPE, extend(this, {clauses: removeByIndex(this.clauses, index)})));
  }

  removeEmptyClauses() {
    return this.validate(Section.create(this.TYPE, extend(this, {clauses: []})));
  }

  removeAllClauses() {
    return this.validate(Section.create(this.TYPE, extend(this, {clauses: []})));
  }

  filterClauses(filterFn): this {
    return (this.validate(
      Section.create(this.TYPE, extend(this, {clauses: this.clauses.filter(filterFn)})),
    ) as unknown) as this;
  }

  setDataGroupIdByResourceType(dataGroupId, resourceType) {
    return this.extendAllClauses((_clauseIndex, clause) => {
      return clause.resourceType === resourceType ? {dataGroupId} : {};
    });
  }

  setDataGroupId(dataGroupId) {
    return this.extendAllClauses(() => ({dataGroupId}));
  }

  extendAllClauses(paramsForClause): this {
    const clauses = this.clauses.map((clause, clauseIndex) => clause.extend(paramsForClause(clauseIndex, clause)));
    return this.validate(Section.create(this.TYPE, extend(this, {clauses})));
  }
}

export class ShowSection extends Section<ShowClause['attrs'], ShowClause> {
  static TYPE = ShowClause.TYPE;
  TYPE = ShowClause.TYPE;

  get valid() {
    return super.valid && this.clauses.every((clause) => clause instanceof ShowClause);
  }

  get hasPerEventFilters() {
    return this.clauses.some(
      (clause) => clause instanceof BehaviorShowClause && clause.behavior.filters && clause.behavior.filters.length,
    );
  }

  _getResourceTypes() {
    return unique(
      this.clauses.map((clause: ShowClauseClassType) =>
        clause instanceof FormulaShowClause ? InsightsResourceType.Formulas : clause.behavior.resourceType,
      ),
    );
  }

  hasEventsQuery() {
    const resourceTypes = this._getResourceTypes();
    return resourceTypes.some((resourceType) => resourceType === InsightsResourceType.Events);
  }

  hasFunnelsQuery() {
    return this.clauses.some(
      (clause) => clause instanceof BehaviorShowClause && clause.behavior.type === MetricType.Funnel,
    );
  }

  isPeopleOnlyQuery() {
    const resourceTypes = this._getResourceTypes();
    const hasPeopleClauses = resourceTypes.some((type) => type === InsightsResourceType.People);
    const hasOnlyFormulasAndPeople = resourceTypes.every(
      (type) => type === InsightsResourceType.People || type === InsightsResourceType.Formulas,
    );

    return hasPeopleClauses && hasOnlyFormulasAndPeople;
  }

  hasPeopleQuery() {
    const resourceTypes = this._getResourceTypes();
    return resourceTypes.some((item) => item === InsightsResourceType.People);
  }

  hasCohortsOverTime() {
    return this._getResourceTypes().includes(InsightsResourceType.Cohorts);
  }

  extendBehaviorClauses(paramsForClause) {
    const clauses = this.getBehaviorClauses().map((clause, clauseIndex) =>
      clause.extend(paramsForClause(clauseIndex, clause)),
    );
    return this.validate(Section.create(this.TYPE, extend(this, {clauses})) as this);
  }

  setDataGroupIdByResourceType(dataGroupId, resourceType) {
    return this.extendBehaviorClauses((_clauseIndex, clause) => {
      return clause.resourceType === resourceType ? {dataGroupId} : {};
    });
  }

  setDataGroupId(dataGroupId) {
    return this.extendBehaviorClauses(() => ({dataGroupId}));
  }

  removeEventFilters() {
    return this.extendBehaviorClauses(() => ({filter: []}));
  }

  makeValidForDataGroup(dataGroupId, customEvents): this | ShowSection {
    let newSection: this | ShowSection = this.filterClauses((clause) => {
      return !this._clauseInvalidForDataGroup({dataGroupId, customEvents, clause});
    });
    if (newSection.clauses.length === 0) {
      newSection = ShowSection.createShowSection();
    }
    return newSection;
  }

  _clauseInvalidForDataGroup({
    dataGroupId,
    customEvents,
    clause,
  }: {
    dataGroupId: string;
    customEvents: any[];
    clause: ShowClauseClassType;
  }) {
    if (clause instanceof FormulaShowClause) {
      return false;
    }

    const {type, name, id, raw_cohort: rawCohort} = clause.behavior;
    const {property} = clause.measurement;
    return (
      (type === MetricType.Cohort &&
        !cohortEntryIsInvalidForDataGroup({
          cohort: {
            id,
            name,
            raw_cohort: rawCohort,
          },
        }),
      dataGroupId) ||
      // Detects People property ("All People" is supported by group-level analysis)
      (type === MetricType.People && property && dataGroupId) ||
      // Custom events that are based on user properties
      (dataGroupId &&
        type === MetricType.CustomEvent &&
        hasUserFilters(
          {
            name,
            id,
          } as CustomEventDefinition,
          customEvents,
        ))
    );
  }

  hasXauClauses() {
    return this.getBehaviorClauses().some((clause) => clause.isXauClause());
  }

  lowestXauTimeUnit() {
    // time unit is the minimum unit of all XAU clauses, e.g. a DAU and WAU report's time unit = `day`
    const xauOperators = this.getBehaviorClauses()
      .map((clause) => clause.measurement.math)
      .filter((math) => XAU_ONLY_MATH_TYPES.includes(math as XAU_MATH_TYPES));
    let unit;
    if (xauOperators.includes(XAU_MATH_TYPES.DAU)) {
      return XAU_MATH_TYPES.DAU;
    } else if (xauOperators.includes(XAU_MATH_TYPES.WAU)) {
      return XAU_MATH_TYPES.WAU;
    } else if (xauOperators.includes(XAU_MATH_TYPES.MAU)) {
      return XAU_MATH_TYPES.MAU;
    } else {
      reporter.error(`Unhandled XAU math type in "${xauOperators}"`);
      return unit;
    }
  }

  hasRollingClauses() {
    return this.getBehaviorClauses().some((clause) => clause.isRollingClause());
  }

  hasCumulativeClauses() {
    return this.getBehaviorClauses().some((clause) => clause.isCumulativeClause());
  }

  getBehaviorClauses(): BehaviorShowClause[] {
    return this.clauses.filter((clause): clause is BehaviorShowClause => clause instanceof BehaviorShowClause);
  }

  getFormulaClauses(): FormulaShowClause[] {
    return this.clauses.filter((clause): clause is FormulaShowClause => clause instanceof FormulaShowClause);
  }

  hasFormulaClause() {
    return this.clauses.some((clause) => clause instanceof FormulaShowClause);
  }

  static createShowSection({
    behavior = {} as BehaviorShowClause['behavior'],
    measurement = {} as BehaviorShowClause['measurement'],
    isExpanded = false,
  } = {}): ShowSection {
    const {
      dataset = null,
      dataGroupId = null,
      type = MetricType.Event,
      resourceType = InsightsResourceType.Events,
    } = behavior;
    const {math} = measurement;
    // when name is `null` it's for `Report#createDefaultReport`,
    // # ShowSection#constructor by default expects the old show clause, need to specific pass in `behavior` and `measurement` to force it to recognize new show clause
    // when name is `undefined` it's for old show clause with `show.value`
    if (behavior.name === null || behavior.name || behavior.id || behavior.raw_cohort) {
      return new ShowSection(
        ShowClause.createShowClause({
          behavior: {
            ...behavior,
            dataset,
            dataGroupId,
            resourceType: resourceType || InsightsResourceType.Events,
            type: type || MetricType.Event,
          },
          measurement: {
            ...measurement,
            math: math || PRIMITIVE_MATH_TYPES.TOTAL,
          },
          isExpanded,
        }),
      );
    }
    return new ShowSection();
  }
}

// Cohort Breakdowns (group bys). Only separate from "GroupSection" for historical reasons
export class CohortsSection extends Section<CohortsClause['attrs'], CohortsClause> {
  static TYPE = CohortsClause.TYPE;
  TYPE = CohortsClause.TYPE;

  get valid() {
    return super.valid && this.clauses.every((clause) => clause instanceof CohortsClause);
  }

  makeValidForDataGroup(dataGroupId) {
    return this.filterClauses((clause) => !cohortEntryIsInvalidForDataGroup({cohort: clause}, dataGroupId));
  }
}

export class GroupSection extends Section<GroupClause['attrs'], GroupClause> {
  static TYPE = GroupClause.TYPE;
  TYPE = GroupClause.TYPE;

  get valid() {
    return super.valid && this.clauses.every(this.validClauseFn);
  }

  validClauseFn(clause) {
    // Allow nested groupsections for value comparisons
    // TODO: Remove GroupSection
    return clause instanceof GroupClause || clause instanceof GroupSection;
  }

  isPeopleTimeSeries() {
    const lastClause = this.clauses.length && this.clauses[this.clauses.length - 1];
    return lastClause.propertyType === `datetime`;
  }

  getGroupByCohortsClauses() {
    const groupByCohortsClauses = this.clauses.filter((clause) => clause.isGroupByCohorts());
    return groupByCohortsClauses.flatMap((clause) => clause.cohorts);
  }

  hasGroupByCohortsClause() {
    return this.clauses.some((clause) => clause.isGroupByCohorts());
  }

  hasGroupByPropertyClause() {
    return this.clauses.some((clause) => !clause.isGroupByCohorts() && !!clause.value);
  }

  hasUserClauses() {
    return this.clauses.some((clause) => this._isUserClause(clause));
  }

  removeUserClauses(dataGroupId) {
    if (dataGroupId) {
      return this.filterClauses((clause) => !this._isUserClause(clause));
    } else {
      return this;
    }
  }

  extendAllClauses(paramsForClause) {
    // Value Comparison allows for nested GroupSections, so recursively extend clauses
    const clauses = this.clauses.map((clauseOrSection, clauseIndex) => {
      if (clauseOrSection instanceof GroupSection) {
        return clauseOrSection.extendAllClauses(paramsForClause);
      } else {
        return clauseOrSection.extend(paramsForClause(clauseIndex, clauseOrSection));
      }
    });

    return this.validate(Section.create(ClauseType.Group, extend(this, {clauses})) as this);
  }

  _isUserClause({resourceType, dataGroupId}: {resourceType?: InsightsResourceType; dataGroupId?: string}) {
    return resourceType === InsightsResourceType.People && !dataGroupId;
  }
}

export class FilterSection extends Section<FilterClause['attrs'], FilterClause> {
  static TYPE = FilterClause.TYPE;
  TYPE = FilterClause.TYPE;
  static DETERMINER_ALL = FILTER_DETERMINER_ALL;
  static DETERMINER_ANY = FILTER_DETERMINER_ANY;
  static DETERMINER_TYPES = [FilterSection.DETERMINER_ALL, FilterSection.DETERMINER_ANY];

  determiner: FilterDeterminer;

  constructor(attrs: any = {}) {
    super(attrs);
    // The determiner could be saved on the attrs object OR on the first filter clause.
    const savedDeterminer = (attrs && attrs.determiner) || (this.clauses.length && this.clauses[0].determiner);
    if (savedDeterminer && FilterSection.DETERMINER_TYPES.includes(savedDeterminer)) {
      this.determiner = savedDeterminer;
    } else {
      this.determiner = FilterSection.DETERMINER_ALL;
    }

    if (this.clauses && this.clauses.length < 2) {
      // reset to all when only one clause
      this.determiner = FilterSection.DETERMINER_ALL;
    }

    if (this.clauses.length) {
      this.clauses[0].determiner = this.determiner;
    }
  }

  get valid() {
    return (
      super.valid &&
      this.clauses.every((clause) => clause instanceof FilterClause) &&
      FilterSection.DETERMINER_TYPES.includes(this.determiner)
    );
  }

  removeMultipleCohortFilters() {
    return this.filterClauses((clause) => !(clause.value === INSIGHTS_COHORT_TYPE && clause.filterValue.length > 1));
  }

  makeValidForDataGroup(dataGroupId) {
    return this.filterClauses((clause) => !this._clauseInvalidForDataGroup({clause, dataGroupId}));
  }

  _clauseInvalidForDataGroup({clause, dataGroupId}) {
    const {resourceType, propertyFilter} = clause;
    const cohort =
      propertyFilter &&
      propertyFilter.filterValue &&
      propertyFilter.filterValue[0] &&
      propertyFilter.filterValue[0].cohort;
    return (
      (cohort && cohortEntryIsInvalidForDataGroup({dataGroupId, cohort})) ||
      (resourceType === InsightsResourceType.People && !clause.dataGroupId)
    );
  }

  serialize() {
    const clauses = super.serialize();
    if (clauses.length > 1) {
      clauses[0].determiner = this.determiner;
    }
    return clauses;
  }

  toBookmarkData() {
    const clauses = super.toBookmarkData();
    if (clauses.length > 1) {
      clauses[0].determiner = this.determiner;
    }
    return clauses;
  }

  toUrlData(): any {
    const clauses = super.toUrlData();
    return clauses.length ? {clauses, determiner: this.determiner} : {};
  }
}

export class FormulaSection extends Section<FormulaClause['attrs'], FormulaClause> {
  static TYPE = FormulaClause.TYPE;
  TYPE = FormulaClause.TYPE;

  get valid() {
    return super.valid && this.clauses.every((clause) => clause instanceof FormulaClause);
  }
}

export class TimeSection extends Section<TimeClause['attrs'], TimeClause> {
  static TYPE = TimeClause.TYPE;
  TYPE = TimeClause.TYPE;

  get valid() {
    return super.valid && this.clauses.length === 1 && this.clauses.every((clause) => clause instanceof TimeClause);
  }
}

export type AllSectionTypes =
  | CohortsSection
  | FilterSection
  | FormulaSection
  | GroupSection
  | ShowSection
  | TimeSection;
